// Presentation helpers for suggestion values: short "what does this mean" descriptions, the
// family grouping for the "browse all" modal, and the curated popular-at-rest sets.
//
// All data is read from taxonomyStore (the bundled suggestionTaxonomy.json by default, overlaid
// at runtime by the server's copy). Derived indexes are cached and rebuilt only when the taxonomy
// version changes, so a runtime overlay takes effect without a rebuild. Genre, mood and
// instrumentation each carry a family taxonomy (grouped); every other dimension is one flat group.
import { getTaxonomy, getTaxonomyVersion, type TaxonomyFamily } from './taxonomyStore'

// Normalize a value for matching, mirroring the Ruby pool's dedupe key (strip spaces,
// underscores, ampersands and hyphens) so "synth-pop" / "synth pop" / "synthpop" all match.
const norm = (s: string) => s.toLowerCase().replace(/[\s_&-]+/g, '')

interface FamilyMeta {
  familyKey: string
  familyLabel: string
  desc?: string
}

// The dimensions that group by family. Order is the description-lookup precedence (a value with
// a desc in more than one is rare; genre wins, as before).
const GROUPED = ['genre', 'instrumentation', 'mood'] as const
type GroupedDim = (typeof GROUPED)[number]

// Derived data, cached and rebuilt when the taxonomy version changes.
let builtVersion = -1
const indexes: Record<GroupedDim, Map<string, FamilyMeta>> = { genre: new Map(), instrumentation: new Map(), mood: new Map() }
const families: Record<GroupedDim, TaxonomyFamily[]> = { genre: [], instrumentation: [], mood: [] }
let descriptions: Record<string, string> = {}

function ensure(): void {
  const v = getTaxonomyVersion()
  if (v === builtVersion) return
  const t = getTaxonomy()
  for (const dim of GROUPED) {
    const idx = new Map<string, FamilyMeta>()
    for (const fam of t[dim].families) {
      for (const e of fam.entries) idx.set(norm(e.value), { familyKey: fam.key, familyLabel: fam.label, desc: e.desc })
    }
    indexes[dim] = idx
    families[dim] = t[dim].families
  }
  descriptions = t.valueDescriptions
  builtVersion = v
}

// A short "what does this mean" line for a suggestion value, or null if none is worth showing.
// Family descriptions (genre/instrument/mood) take precedence over the hand-written map (chiefly
// the opaque music-theory terms in the harmony pool).
export function describeValue(value: string): string | null {
  ensure()
  const n = norm(value)
  for (const dim of GROUPED) {
    const meta = indexes[dim].get(n)
    if (meta?.desc) return meta.desc
  }
  return descriptions[value] ?? null
}

// The curated popular-at-rest set for a dimension (genre/mood), or null if it has none — then the
// picker leads with the backend's genre-conditioned pool instead.
export function popularFor(dimension: string | null): string[] | null {
  if (dimension !== 'genre' && dimension !== 'mood' && dimension !== 'instrumentation') return null
  ensure()
  const popular = getTaxonomy()[dimension].popular
  return popular.length ? popular : null
}

export interface SuggestionGroup {
  key: string
  label: string
  description?: string
  items: string[]
}

// Group a dimension's suggestion pool for the "browse all" modal. Genre, mood and instrumentation
// group by family (in canonical family order, preserving pool order within each family; unknowns
// fall under "More"). Every other dimension is one flat group carrying its purpose as description.
export function groupSuggestions(
  dimension: string | null,
  values: string[],
  fallbackLabel: string,
  fallbackDescription?: string,
): SuggestionGroup[] {
  ensure()
  const dim = dimension as GroupedDim | null
  const index = dim && GROUPED.includes(dim) ? indexes[dim] : null
  const fams = dim && GROUPED.includes(dim) ? families[dim] : null

  if (!index || !fams) {
    return [{ key: dimension ?? 'all', label: fallbackLabel, description: fallbackDescription, items: values }]
  }

  const byFamily = new Map<string, string[]>()
  const other: string[] = []
  for (const v of values) {
    const g = index.get(norm(v))
    if (g) {
      const arr = byFamily.get(g.familyKey) ?? []
      arr.push(v)
      byFamily.set(g.familyKey, arr)
    } else {
      other.push(v)
    }
  }

  const groups: SuggestionGroup[] = []
  for (const fam of fams) {
    const items = byFamily.get(fam.key)
    if (items?.length) groups.push({ key: fam.key, label: fam.label, items })
  }
  if (other.length) groups.push({ key: 'other', label: 'More', items: other })
  return groups
}
